import React, { useCallback } from 'react';
import { arrayPop, arrayPush, change, Field } from 'redux-form';
import { find, get } from 'lodash';
import moment from 'moment';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import Calendar from '../../../api/api-calendar';
import Button from '../elements-buttons/Button';
import { Helpers } from 'shared-components/helpers/helpers';
import { MobileDateTimePicker } from 'shared-components/trays/date-time/datetime-tray';
import { TimeData } from 'shared-components/search-module/components/date-time/mobile/data/time-data';

const MobileDateTime = (props) => {
  const formatSchedule = useCallback((date) => {
    return date ? Helpers.parseDate(moment(date)) : null;
  }, []);

  const formatScheduleInput = useCallback((date) => {
    return date ? moment(date).format('MMM, D, YYYY') : null;
  }, []);

  const formatTime = useCallback((timeIndex) => {
    const timeObject = find(TimeData, { time_index: timeIndex });
    return timeObject ? {
      h: get(timeObject, 'h'),
      m: get(timeObject, 'm')
    } : null;
  }, []);

  const validateBeforeFeeCalc = (index, newSchedule) => {
    const { schedule, getFeeCalc } = props;
    const scheduleToUse = newSchedule || schedule;
    const start = get(scheduleToUse, 'start.time', null);
    const end = get(scheduleToUse, 'end.time', null);
    if (start && end) {
      return getFeeCalc(null, true);
    }
  };

  const handleReset = async (index) => {
    const { dispatch, formName, getFeeCalc } = props;

    await dispatch(change(formName, `schedule[${index}]`, {}));
    await dispatch(change(formName, `scheduleInput[${index}]`, {}));

    return getFeeCalc(null, true);
  };

  const handleDateChange = async (date, index) => {
    const { dispatch, formName, schedule } = props;

    const start = get(schedule, `[${index}].start`, {});
    const end = get(schedule, `[${index}].end`, {});

    const startDate = get(start, 'date', {});
    const endDate = get(end, 'date', {});

    const startMomentObj = moment(startDate.m + '-' + startDate.d + '-' + startDate.y, 'MM-DD-YYYY');
    const endMomentObj = moment(endDate.m + '-' + endDate.d + '-' + endDate.y, 'MM-DD-YYYY');
    const wasOvernight = endMomentObj.isAfter(startMomentObj);

    const formattedEndSchedule = wasOvernight
      ? formatSchedule(moment(date).add(1, 'day').toDate())
      : formatSchedule(date);
    const formattedStartSchedule = formatSchedule(date);
    const formattedScheduleInput = formatScheduleInput(date);

    await dispatch(change(formName, `schedule[${index}].start.date`, formattedStartSchedule));
    await dispatch(change(formName, `schedule[${index}].end.date`, formattedEndSchedule));
    await dispatch(change(formName, `scheduleInput[${index}].date`, formattedScheduleInput));

    const newSchedule = {
      start: {
        ...start,
        date: formattedStartSchedule
      },
      end: {
        ...end,
        date: formattedEndSchedule
      }
    };
    return validateBeforeFeeCalc(index, newSchedule);
  };

  const handleTimeChange = async (time, index) => {
    const { dispatch, formName, schedule } = props;
    const start = formatTime(get(time, 'startTime', null));
    const end = formatTime(get(time, 'endTime', null));
    const isOvernight = get(find(TimeData, { time_index: get(time, 'endTime', null) }), 'overnight', false);
    if (isOvernight) {
      const prevDate = getDateValue(index);
      if (prevDate) {
        const formattedDate = formatSchedule(moment(prevDate).add(1, 'day').toDate());
        await dispatch(change(formName, `schedule[${index}].end.date`, formattedDate));
      }
    }
    await dispatch(change(formName, `schedule[${index}].start.time`, start));
    await dispatch(change(formName, `schedule[${index}].end.time`, end));
    await dispatch(change(formName, `scheduleInput[${index}].endTime`, end));
    await dispatch(change(formName, `scheduleInput[${index}].startTime`, start));
    const prevStart = get(schedule, `[${index}].start`, {});
    const prevEnd = get(schedule, `[${index}].end`, {});
    const newSchedule = {
      start: {
        ...prevStart,
        time: start
      },
      end: {
        ...prevEnd,
        time: end
      }
    };
    return validateBeforeFeeCalc(index, newSchedule);
  };

  const extendDay = async (e) => {
    e.preventDefault();
    const { dispatch, formName, fields, schedule, getFeeCalc } = props;

    const lastDateTime = schedule[schedule.length - 1];
    const date = get(lastDateTime, 'start.date');

    if (!date) {
      return;
    }
    const startTime = get(lastDateTime, 'start.time', {});
    const endTime = get(lastDateTime, 'end.time', {});
    const day = moment(date.m + '-' + date.d + '-' + date.y, 'MM-DD-YYYY');
    const addDate = day.startOf('day').add(1, 'd').toDate();

    fields.push({
      date: formatScheduleInput(addDate),
      startTime,
      endTime
    });
    await dispatch(
      arrayPush(
        formName,
        'schedule',
        {
          start: {
            date: formatSchedule(addDate),
            time: startTime
          },
          end: {
            date: formatSchedule(addDate),
            time: endTime
          }
        }
      )
    );

    return getFeeCalc(null, false);
  };

  const deleteDay = async (e) => {
    const { dispatch, formName, fields, getFeeCalc } = props;

    e.preventDefault();
    fields.pop();
    await dispatch(arrayPop(formName, 'schedule'));
    return getFeeCalc(null, false);
  };

  const getDateValue = (index) => {
    const scheduleForDay = get(props, `schedule[${index}].start.date`);
    if (scheduleForDay) {
      const { y, m, d } = scheduleForDay;
      return new Date(y, m - 1, d);
    }
    return null;
  };

  const getTimeValue = (index) => {
    const startSchedule = get(props, `schedule[${index}].start.time`);
    const endSchedule = get(props, `schedule[${index}].end.time`);
    let startTime;
    let endTime;

    if (startSchedule) {
      const { h, m } = startSchedule;
      startTime = get(find(TimeData, { h, m }), 'time_index', null);
    }

    if (endSchedule) {
      const { h, m } = endSchedule;
      endTime = get(find(TimeData, { h, m }), 'time_index', null);
    }
    return {
      startTime, endTime
    };
  };

  const loadAvailability = (query, cb) => {
    Calendar.readAnonymous(query, cb);
  };

  return (
    <>
      {props.fields.map((field, index) => (
        <div
          className={classNames({
            'padding-lg-top': 0 < index,
            'padding-md-top': 0 === index,
            'padding-lg-bottom': props.fields.length > 1 && index < props.fields.length - 1
          })}
          key={`${field}`}
        >
          <Field
            {...props}
            name={field}
            component={MobileDateTimePicker}
            dateValue={getDateValue(index)}
            setDateValue={handleDateChange}
            timeValue={getTimeValue(index)}
            setTimeValue={handleTimeChange}
            loadAvailability={loadAvailability}
            handleReset={handleReset}
            index={index}
            clearable
            isReduxForm
          />
        </div>
      ))}
      <div>
        {
          !props.reachedMaxDays &&
          <Button
            className="text-small btn-link-secondary remove-click-styling no-gutter"
            onClick={extendDay}
            type="button"
            attributes={{ 'data-testing-id': 'extend-a-day' }}
          >
            <FormattedMessage
              id="inbox_guest_update_booking.booking_details.extend_a_day"
              defaultMessage="Extend a day"
            />
          </Button>
        }

        {
          1 < props.fields.length ?
            <Button
              className="text-small btn-link remove-click-styling pull-right no-gutter"
              onClick={deleteDay}
              type="button"
            >
              <FormattedMessage
                id="inbox_guest_update_booking.booking_details.remove"
                defaultMessage="Remove"
              />
            </Button>
            :
            null
        }
      </div>
    </>
  );
};

export default MobileDateTime;
